import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import './BedbugTraps.css';

const BedbugTraps = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const fetchData = async () => {
    try {
      const [devRes, alertRes] = await Promise.all([
        fetch(`${config.TRAP_API_BASE_URL}/api/devices`),
        fetch(`${config.TRAP_API_BASE_URL}/api/alerts`),
      ]);
      if (devRes.ok) setDevices(await devRes.json());
      if (alertRes.ok) setAlerts(await alertRes.json());
    } catch (error) {
      console.error('Error fetching trap data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, []);

  const getAlertForDevice = (deviceId) => alerts.find(a => a.device_id === deviceId);

  const handleAcknowledge = async (deviceId) => {
    try {
      setAlerts(prev => prev.filter(a => a.id !== deviceId && a.device_id !== deviceId));
      const response = await fetch(`${config.TRAP_API_BASE_URL}/api/acknowledge/${deviceId}`, {
        method: 'POST',
      });
      if (response.ok) fetchData();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      fetchData();
    }
  };

  const activeCount = devices.filter(d => d.alert_active).length;
  const bedbugDevices = devices.filter(d => d.unit_type === 'versa_bedbug');
  const rodentDevices = devices.filter(d => d.unit_type === 'versa_rodent');

  const renderDeviceCard = (device) => {
    const alert = getAlertForDevice(device.device_id);
    const isAlert = !!alert;
    const isAutoReg = device.is_auto_registered;

    return (
      <div
        key={device.device_id}
        className={`card ${isAlert ? 'active-alert-card' : ''}`}
        style={{
          borderLeft: isAlert
            ? '4px solid #f43f5e'
            : isAutoReg
            ? '4px solid #f59e0b'
            : '4px solid #22c55e',
        }}
      >
        <div className="card-content">
          <div className="left-section">
            <div className="device-name">{device.device_id}</div>

            <div className="status-container">
              <div
                className={`status-dot ${isAlert ? 'pulsing-red-dot' : ''}`}
                style={{ backgroundColor: isAlert ? '#f43f5e' : '#22c55e' }}
              />
              <div
                className="status-text"
                style={{ color: isAlert ? '#f43f5e' : '#22c55e', fontWeight: '600' }}
              >
                {isAlert ? '🚨 ALERT ACTIVE' : '✅ MONITORING'}
              </div>
            </div>

            {isAlert && alert.triggered_at && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#f43f5e', fontWeight: '600' }}>
                {alert.event_type || 'Alert'} | {new Date(alert.triggered_at).toLocaleString()}
              </div>
            )}

            {isAutoReg ? (
              <div style={{ marginTop: '6px', fontSize: '11px', color: '#f59e0b', fontWeight: '600' }}>
                ⚡ Auto-registered device
              </div>
            ) : (
              <div className="device-location-label" style={{ marginTop: '8px', fontSize: '13px', color: '#64748b' }}>
                📍 {device.ship_name} • {device.location_section} • Cabin {device.area_cabin}
              </div>
            )}

            {device.last_seen && (
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#94a3b8' }}>
                Last seen: {new Date(device.last_seen).toLocaleString()}
              </div>
            )}
          </div>

          <div className="right-section">
            <div className="device-image">
              <img
                src={device.unit_type === 'versa_rodent' ? '/assets/RodentTrap.png' : '/assets/BedbugTrap.png'}
                alt={device.unit_type === 'versa_rodent' ? 'Rodent Trap' : 'Bedbug Trap'}
                className="hardware-icon"
              />
            </div>
          </div>
        </div>

        <button
          className="view-button"
          onClick={() => setSelectedDevice({
            id: device.device_id,
            name: device.device_id,
            ship: device.ship_name,
            dept: device.department,
            loc: device.location_section,
            area: device.area_cabin,
            text: device.location_text,
            unit_type: device.unit_type,
          })}
        >
          <span className="view-button-text">View</span>
          <span className="view-button-arrow">›</span>
        </button>
      </div>
    );
  };

  return (
    <div className="bedbug-safe-area">
      <div className="bedbug-container">
        <div className="bedbug-content">
          {/* Header */}
          <div className="bedbug-header-row">
            <button className="back-button" onClick={() => navigate('/')}>
              <span className="back-icon">‹</span>
            </button>
            <div className="header-title-group">
              <div className="header-title-main">TRAP MONITORING</div>
              <div className="header-title-sub">VERSA NETWORK</div>
            </div>
            <button
              onClick={() => navigate('/trap-alert-history')}
              style={{ marginLeft: 'auto', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#fff', padding: '8px 12px', color: '#334155', cursor: 'pointer' }}
            >
              Alert history
            </button>
            {activeCount > 0 && (
              <div style={{
                background: '#f43f5e',
                color: '#fff',
                borderRadius: '999px',
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '700',
              }}>
                {activeCount} ALERT{activeCount > 1 ? 'S' : ''}
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading-state">Connecting to Versa Network...</div>
          ) : devices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              No devices registered yet.
            </div>
          ) : (
            <div className="devices-container">
              {alerts.length > 0 && (
                <section aria-label="Active alert queue" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                    <strong>Active alert queue</strong>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>{alerts.length} pending</span>
                  </div>
                  {alerts.map((alert, index) => (
                    <div key={alert.id ?? `${alert.device_id}-${alert.triggered_at}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 0', borderTop: '1px solid #e2e8f0' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#b91c1c' }}>{index + 1}. {alert.event_type} | {alert.device_id}</div>
                        <div style={{ color: '#64748b', fontSize: '13px', marginTop: '3px' }}>{new Date(alert.triggered_at).toLocaleString()}</div>
                      </div>
                      <button
                        onClick={() => handleAcknowledge(alert.id ?? alert.device_id)}
                        style={{ flexShrink: 0, border: '1px solid #cbd5e1', borderRadius: '6px', background: '#fff', padding: '7px 10px', cursor: 'pointer' }}
                      >
                        Acknowledge
                      </button>
                    </div>
                  ))}
                </section>
              )}

              {/* Bedbug Traps Section */}
              {bedbugDevices.length > 0 && (
                <>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', marginBottom: '8px', marginTop: '8px' }}>
                    BEDBUG TRAPS ({bedbugDevices.length})
                  </div>
                  {bedbugDevices.map(renderDeviceCard)}
                </>
              )}

              {/* Rodent Traps Section */}
              {rodentDevices.length > 0 && (
                <>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', marginBottom: '8px', marginTop: '16px' }}>
                    RODENT TRAPS ({rodentDevices.length})
                  </div>
                  {rodentDevices.map(renderDeviceCard)}
                </>
              )}

            </div>
          )}
        </div>
      </div>

      {/* Detail Overlay */}
      {selectedDevice && (() => {
        const activeAlert = getAlertForDevice(selectedDevice.id);
        const isDeviceInAlert = !!activeAlert;

        return (
          <div className="modal-overlay" onClick={() => setSelectedDevice(null)}>
            <div className="phone-wrapper" onClick={(e) => e.stopPropagation()}>
              <div className="phone-notch">
                <div className="camera-dot"></div>
              </div>
              <button className="phone-close-btn" onClick={() => setSelectedDevice(null)}>✕</button>
              <header className="phone-header">
                <h1 className="phone-title">GLOBAL ALERTZ</h1>
                <p className="phone-subtitle">TRAP MONITORING SYSTEM</p>
              </header>
              <main className="phone-content">
                {isDeviceInAlert ? (
                  <div className="alert-container">
                    <div className="alert-header">
                      <div className="alert-label">ALERT</div>
                      <div className={`alert-type-badge ${selectedDevice.unit_type === 'versa_rodent' ? 'rodent' : 'bedbug'}`}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        {selectedDevice.unit_type === 'versa_rodent' ? '🐀 Rodent Trap' : '🐛 Bedbug Trap'}
                      </div>
                    </div>
                    <div className="details-stack">
                      <div className="detail-box">
                        <span className="detail-label">Device ID</span>
                        <span className="detail-val">{selectedDevice.id}</span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Event Type</span>
                        <span className="detail-val">{activeAlert.event_type || 'Trap detection'}</span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Alert Date &amp; Time</span>
                        <span className="detail-val">
                          {activeAlert.triggered_at
                            ? new Date(activeAlert.triggered_at).toLocaleString()
                            : 'Unavailable'}
                        </span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Ship Name</span>
                        <span className="detail-val">{selectedDevice.ship}</span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Department</span>
                        <span className="detail-val">{selectedDevice.dept}</span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Section</span>
                        <span className="detail-val">{selectedDevice.loc}</span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Cabin</span>
                        <span className="detail-val">{selectedDevice.area}</span>
                      </div>
                      <div className="detail-box">
                        <span className="detail-label">Location Description</span>
                        <p className="detail-val desc">{selectedDevice.text}</p>
                      </div>
                    </div>
                    <button
                      className="ack-btn"
                      onClick={() => {
                        handleAcknowledge(activeAlert.id ?? selectedDevice.id);
                        setSelectedDevice(null);
                      }}
                    >
                      Acknowledge
                    </button>
                  </div>
                ) : (
                  <div className="idle-container">
                    <div className="radar-circle">
                      <div className="radar-pulse"></div>
                      <div className="radar-dot"></div>
                    </div>
                    <h2 className="idle-title" style={{ color: '#fff', fontSize: '1.2rem' }}>{selectedDevice.name} Normal</h2>
                    <p className="idle-text" style={{ marginBottom: '24px' }}>Trap is online. No events detected.</p>
                    <div style={{ fontSize: '12px', color: '#64748b', padding: '0 16px', textAlign: 'center' }}>
                      📍 {selectedDevice.ship} • {selectedDevice.loc} • Cabin {selectedDevice.area}
                    </div>
                  </div>
                )}
              </main>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BedbugTraps;
